import { useState, useEffect, useCallback } from 'react';
import { supabase, handleSupabaseError, retryOperation } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Comment } from '../types/comment';

export function useBlogComments(blogPostId: string | undefined) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!blogPostId) {
      setComments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await retryOperation(
        () => supabase
          .from('blog_comments')
          .select(`
            id,
            blog_post_id,
            user_id,
            content,
            created_at,
            updated_at,
            user_profile:gardener_profiles(
              first_name,
              last_name
            )
          `)
          .eq('blog_post_id', blogPostId)
          .order('created_at', { ascending: true }),
        3,
        1000
      );

      if (fetchError) throw fetchError;
      setComments(data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
      const errorMessage = handleSupabaseError(err);
      setError(errorMessage);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [blogPostId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const addComment = async (content: string) => {
    if (!user || !blogPostId) {
      throw new Error('Must be logged in to comment');
    }

    if (!content.trim()) {
      throw new Error('Comment cannot be empty');
    }

    setSubmitting(true);
    setError(null);

    try {
      const { data, error: insertError } = await retryOperation(
        () => supabase
          .from('blog_comments')
          .insert({
            blog_post_id: blogPostId,
            user_id: user.id,
            content: content.trim()
          })
          .select(`
            id,
            blog_post_id,
            user_id,
            content,
            created_at,
            updated_at,
            user_profile:gardener_profiles(
              first_name,
              last_name
            )
          `)
          .single(),
        3,
        1000
      );

      if (insertError) throw insertError;

      // Add the new comment to the local state
      setComments(prev => [...prev, data]);
      
      return data;
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = handleSupabaseError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!user) {
      throw new Error('Must be logged in to delete comments');
    }

    try {
      const { error: deleteError } = await retryOperation(
        () => supabase
          .from('blog_comments')
          .delete()
          .eq('id', commentId)
          .eq('user_id', user.id), // Ensure user can only delete their own comments
        3,
        1000
      );

      if (deleteError) throw deleteError;

      // Remove the comment from local state
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
      const errorMessage = handleSupabaseError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  return {
    comments,
    loading,
    error,
    submitting,
    addComment,
    deleteComment,
    refetch: fetchComments
  };
}